import { useState, useEffect, useContext, useRef } from "react";
import { FormatStep } from "./FormatStep";
import { BasicInfoStep } from "./BasicInfoStep";
import { StoryDetailsStep } from "./StoryDetailsStep";
import { RelationshipsStep } from "./RelationshipsStep";
import { LocationsAndFavoritesStep } from "./LocationsAndFavoritesStep";
import { NarratorStep, voices } from "./NarratorStep"; 
import { AvatarStep } from "./AvatarStep";
import { AudioPreferencesStep } from "./AudioPreferencesStep";
import { UserContext } from "../UserContext";
import AnimatedSummaryText from "./AnimatedSummaryText";
import GenerationOverlay from "./GenerationOverlay";
import api from "../../api/apiClient";
import { Link, useNavigate } from 'react-router-dom';
import { trackEvent } from "../../utils/pixel";


const getLanguageName = (code) => {
  const langMap = {
    sv: "svenska",
    ar: "arabiska",
    fi: "finska",
    en: "engelska",
    bs: "bosniska",
    so: "somaliska",
  };
  return langMap[code] || code;
};

const sanitizeLogs = (logs = []) => {
  const cleanLogs = [];
  const imageUrls = [];

  for (const entry of logs) {
    if (typeof entry === "string") {
      cleanLogs.push(entry);
      const match = entry.match(/https?:\/\/cdn\.leonardo\.ai\/[^\s"]+/);
      if (match) {
        imageUrls.push(match[0]);
      }
    }

    if (
      typeof entry === "object" &&
      entry !== null &&
      entry.type === "image" &&
      typeof entry.url === "string"
    ) {
      cleanLogs.push(entry);
      imageUrls.push(entry.url);
    }
  }

  return {
    cleanLogs,
    imageUrls: [...new Set(imageUrls)],
  };
};

export const StoryForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    format: "",
    childName: "",
    childAge: 0,
    gender: "",
    theme: "",
    emotion: "",
    interests: "",
    friends: "",
    favoritePlace: "",
    siblings: "",
    parents: "",
    dedication: "", 
    grandparents: "",
    favoriteAnimal: "",
    city: "",
    avatar: "",
    duration: 5000,
    narrator: "", 
  });

  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [showOverlay, setShowOverlay] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [summaryAudioUrl, setSummaryAudioUrl] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const audioRef = useRef(null);
  const formRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const [voicePreviews, setVoicePreviews] = useState({});
  const [loadingVoices, setLoadingVoices] = useState(false);
  const { currentUser, updateCredits, addNotification } = useContext(UserContext);
  const navigate = useNavigate();


  const scrollToTop = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
    scrollToTop();
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    scrollToTop();
  };

  const handleUpdate = (key, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      if (key === "avatar") {
        updated.avatar = value;
      } else {
        updated[key] = value;
      }
      if (key === "format" && (value === "pdf" || value === "both")) {
        updated.duration = 5000;
        updated.credits = 600;
      }
      return updated;
    });
  };

  const getSteps = () => {
    return [
      BasicInfoStep,
      StoryDetailsStep,
      RelationshipsStep,
      LocationsAndFavoritesStep,
      NarratorStep,
      AvatarStep,
    ];
  };

  const steps = getSteps();
  const totalSteps = steps.length;

  useEffect(() => {
    if (currentStep === 2 && formData.childName) {
      setLoadingVoices(true);
      (async () => {
        const previews = {};
        for (const voice of voices) {
          try {
            const { data: blob } = await api.post(
              "/preview-voice",
              {
                voice_name: voice.value,
                text: `Följ med, ${formData.childName} , på ett magiskt äventyr. Genom en värld full av under, och överraskningar.`,
                fade_in: 2000,
                fade_out: 2000
              },
              { responseType: "blob" }
            );
            
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            
            await new Promise((resolve, reject) => {
              audio.addEventListener("canplaythrough", resolve, { once: true });
              audio.addEventListener("error", reject, { once: true });
              audio.load();
            });
            
            previews[voice.value] = audio;
          } catch (err) {
            console.warn("Kunde inte förladda röst:", voice.value, err);
          }
        }
        setVoicePreviews(previews);
        setLoadingVoices(false);
      })();
    }
  }, [currentStep, formData.childName]);

  const handleStartGeneration = async () => {
    if (!currentUser) {
      
      return;
    }

    const summary = generateStaticSummary(formData);
    const dataToSubmit = {
      ...formData,
      name: formData.childName,
      bookType:
        formData.format === "bundle"
          ? "both"
          : formData.format === "print"
          ? "print"
          : "digital",
      user_id: currentUser.id,
      staticSummary: summary,
    };

    try {
      await api.post("/init-book", dataToSubmit);
      // “Lead” (bokstart) i Facebook Pixel
    if (window.fbq) {
      trackEvent("Lead");
    }
      

    } catch (error) {
      console.error("Error starting generation:", error);
     
    }
  };
  
    

  const handleAvatarConfirmed = async () => {
    const staticSummary = generateStaticSummary(formData);
    const language = "sv";

    try {
      // 1. GPT mini-berättelse
      const { data: storyData } = await api.post("/create-mini-story", {
        static_summary: staticSummary,
        language,
      });
      const miniStory = storyData.mini_story;
      setSummaryText(miniStory);

      // 2. TTS-ljud
      const { data: audioBlob } = await api.post(
        "/generate-summary-audio",
        { summary: miniStory, voice_name: formData.narrator },
        { responseType: "blob" }
      );
      const blob = new Blob([audioBlob], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      setSummaryAudioUrl(url);

      // 3. Visa overlay
      setShowOverlay(true);

      // 4. Starta bokgenerering
      await handleStartGeneration();

    } catch (err) {
      console.error("❌ Fel i miniberättelse/ljudflödet:", err);
     
    }
  };

  useEffect(() => {
    if (summaryAudioUrl && audioRef.current) {
      audioRef.current.play();
      const timer = setTimeout(() => {
        setShowSummary(true);
      }, 12000);
  
      audioRef.current.onended = () => {
        console.log("🔊 Ljudet är slut – redirectar snart...");
        setTimeout(() => {
          window.location.href = "/";
        }, 4000);
      };


  
      return () => clearTimeout(timer);
    }
  }, [summaryAudioUrl]);
  
  useEffect(() => {
    if (!showOverlay || !currentUser?.id) return;
  
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/logs/${currentUser.id}`);
        const data = await res.json();
  
        if (data.logs) {
          const { cleanLogs, imageUrls } = sanitizeLogs(data.logs);
          setLogEntries((prev) => {
            const combined = [...prev, ...cleanLogs];
            return [...new Map(combined.map((item) => [JSON.stringify(item), item])).values()];
          });
          setImageUrls((prev) => [...new Set([...prev, ...imageUrls])]);
        }
      } catch (err) {
        console.error("Kunde inte hämta loggar:", err);
      }
    }, 3000);
  
    return () => clearInterval(interval);
  }, [showOverlay, currentUser]);

  const generateStaticSummary = (formData) => {
    const {
      childName,
      childAge,
      gender,
      theme,
      interests,
      friends,
      siblings,
      parents,
      grandparents,
      favoritePlace,
      favoriteAnimal,
      city,
      avatar,
      duration,
      narrator,
    } = formData;

    const name = childName || "Robin";
    const pronoun = gender === "boy" ? "han" : "hon";
    const genderText = gender === "boy" ? "pojke" : "flicka";
    const narratorText = narrator.includes("B") ? "WaveNet B"
                      : narrator.includes("C") ? "WaveNet C"
                      : narrator.includes("D") ? "WaveNet D"
                      : "WaveNet A";

    let summary = `Du har valt att skapa en ${formData.format === "audiobook" ? "ljudbok" : "bok"} där hjälten är ${name}`;
    summary += childAge ? `, en ${childAge} år gammal ${genderText}` : `, en ${genderText}`;
    summary += ".";

    if (theme) summary += ` Äventyret utspelar sig med temat "${theme.toLowerCase()}".`;
    if (interests) summary += ` ${name} tycker om att ${interests.toLowerCase()}.`;
    if (friends) summary += ` Några av ${pronoun}s vänner är ${friends}.`;
    if (siblings) summary += ` ${pronoun.charAt(0).toUpperCase() + pronoun.slice(1)} har syskon som heter ${siblings}.`;
    if (parents) summary += ` Föräldrarna heter ${parents}.`;
    if (grandparents) summary += ` Mor- och farföräldrarna är ${grandparents}.`;
    if (favoritePlace) summary += ` En plats ${pronoun} gillar extra mycket är ${favoritePlace}.`;
    if (favoriteAnimal) summary += ` Favoritdjuret är en ${favoriteAnimal}.`;
    if (city) summary += ` ${name} bor i ${city}.`;
    if (avatar) summary += ` Avataren som används i berättelsen är ${avatar}.`;
    if (formData.format === "audiobook" && duration) summary += ` Ljudboken är ungefär ${duration} minuter lång.`;
    if (formData.format === "audiobook" && narrator) summary += ` Den läses upp av ${narratorText} berättarröst.`;

    return summary.trim();
  };

  const CurrentStepComponent = steps[currentStep];

  return (
    <div 
      ref={formRef}
      className="py-12 px-4 bg-gradient-to-b from-warmWhite to-white flex justify-center"
    >
      <div className="form-container bg-white rounded-lg shadow-lg p-6 m-6 max-w-3xl w-full">
        <div className="mb-8">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue rounded-full"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
          <p className="text-sm text-center mt-2 text-gray-600">
            Steg {currentStep + 1} av {totalSteps}
          </p>
        </div>

        <div className="step-transition">
          <CurrentStepComponent
            data={formData}
            onUpdate={handleUpdate}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={handleStartGeneration}
            onStartGeneration={handleStartGeneration}
            voicePreviews={voicePreviews}
            loadingVoices={loadingVoices}
            onAvatarConfirmed={handleAvatarConfirmed} 
          />
        </div>

        <div className="flex justify-between mt-8">
          {currentStep > 0 && (
            <button 
              className="px-4 py-2 border rounded-lg hover:bg-gray-100" 
              onClick={handleBack}
            >
              Tillbaka
            </button>
          )}
          <div className="flex-1" />
        </div>
      </div>
{summaryAudioUrl && <audio ref={audioRef} src={summaryAudioUrl} />}

      {showOverlay && (
      <GenerationOverlay 
        summaryText={summaryText}
        audioUrl={summaryAudioUrl}
      />
    )}

      
    </div>
  );
};

export default StoryForm;